/**
 * Which install guide a visitor needs, and how a signature gets from the desk to the phone.
 *
 * The page opens on the guide for the device it is running on, so an operator who opens it on the
 * iPhone lands on the iPhone steps with the copy button first — the phone is where the mobile
 * signatures are installed, because every mobile signature field takes a PASTE and nothing else.
 * Getting there from the desk is one link to the saved signature (`?id=`), sent by mail, copied or
 * shared (AirDrop), plus the guide to open (`?install=`).
 *
 * Pure: the page hands in `navigator.userAgent` and `navigator.maxTouchPoints`.
 */

/** The clients the install guide covers, in the order the team uses them. */
export const SIGNATURE_CLIENTS = [
  'appleMac',
  'appleIos',
  'thunderbird',
  'outlookClassic',
  'outlookNew',
  'mobileApps',
] as const;

/** One install guide on the page. */
export type SignatureClient = (typeof SIGNATURE_CLIENTS)[number];

/** The URL parameter naming the install guide to open. */
export const INSTALL_CLIENT_PARAM = 'install';

/** What the page needs to know about the device it runs on. */
export interface SignatureDevice {
  /** A phone or tablet: the page leads with the install guide, and there is no phone to send to. */
  readonly handheld: boolean;
  /** The guide to open when the URL names none. */
  readonly client: SignatureClient;
  /**
   * Safari on a Mac. Mail on the same Mac should not be installed from it: a Safari copy can put a
   * web archive on the clipboard, which carries the logo's IMAGE DATA along, and Mail attaches
   * image data it is given to every mail. Chrome, Firefox and Edge put plain HTML there, in which
   * the logo stays a link.
   */
  readonly macSafari: boolean;
}

/**
 * Reads the device from its user agent.
 *
 * iPadOS asks for desktop sites by default and then reports itself as a Mac; the touch points are
 * what tell the two apart. Windows opens on Thunderbird, the first of the team's Windows clients.
 */
export function detectSignatureDevice(
  userAgent: string,
  maxTouchPoints: number,
): SignatureDevice {
  const ios =
    /iPhone|iPad|iPod/.test(userAgent) ||
    (/Macintosh/.test(userAgent) && maxTouchPoints > 1);
  const android = /Android/.test(userAgent);
  const mac = !ios && /Macintosh|Mac OS X/.test(userAgent);
  const macSafari =
    mac &&
    /Safari\//.test(userAgent) &&
    !/Chrome|Chromium|Edg\/|OPR\/|Firefox/.test(userAgent);
  const client: SignatureClient = ios
    ? 'appleIos'
    : android
      ? 'mobileApps'
      : /Windows/.test(userAgent)
        ? 'thunderbird'
        : 'appleMac';
  return { handheld: ios || android, client, macSafari };
}

/** The install guide a URL parameter names, or null for a missing or unknown one. */
export function parseSignatureClient(
  value: string | null,
): SignatureClient | null {
  return SIGNATURE_CLIENTS.find((client) => client === value) ?? null;
}

/**
 * The link that opens a SAVED signature on another device, on the given install guide.
 *
 * Built from the current page URL so it keeps the dashboard's host and path. Anything else in the
 * query is dropped: the link names one signature and one guide, nothing that was on screen.
 */
export function signatureInstallLink(
  pageUrl: string,
  signatureId: string,
  client: SignatureClient,
): string {
  const url = new URL(pageUrl);
  url.search = '';
  url.hash = '';
  url.searchParams.set('id', signatureId);
  url.searchParams.set(INSTALL_CLIENT_PARAM, client);
  return url.toString();
}

/** A `mailto:` that sends the install link to the operator's own address. */
export function signatureInstallMailto(
  to: string,
  subject: string,
  body: string,
): string {
  // `@` stays literal: some clients do not decode `%40` in the address part.
  const address = encodeURIComponent(to).replace(/%40/g, '@');
  return `mailto:${address}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
